/**
 * Geolocation utility using ipinfo.io API
 * Determines user location to control cookie banner visibility
 * US users automatically have cookies accepted and banner hidden
 */

const API_TOKEN = import.meta.env.VITE_IPINFO_TOKEN;
const API_ENDPOINT = 'https://api.ipinfo.io/lite/me';
const CACHE_KEY = 'ally_user_location';
const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
const US_COUNTRY_CODE = 'US';

// Validate that the API token is available
if (!API_TOKEN) {
  console.warn(
    '⚠️ VITE_IPINFO_TOKEN environment variable is not set. Geolocation functionality will be disabled.'
  );
}

/**
 * Get cached location data from localStorage
 * @returns {Object|null} Cached location data or null if expired/not found
 */
function getCachedLocation() {
  if (typeof window === 'undefined' || !window.localStorage) {
    return null;
  }

  try {
    const cached = window.localStorage.getItem(CACHE_KEY);
    if (!cached) {
      return null;
    }

    const { data, timestamp } = JSON.parse(cached);
    const now = Date.now();

    // Check if cache is still valid (24 hours)
    if (now - timestamp < CACHE_DURATION) {
      return data;
    }

    // Cache expired, remove it
    window.localStorage.removeItem(CACHE_KEY);
    return null;
  } catch (error) {
    console.warn('Error reading cached location:', error);
    return null;
  }
}

/**
 * Cache location data in localStorage
 * @param {Object} data Location data to cache
 */
function cacheLocation(data) {
  if (typeof window === 'undefined' || !window.localStorage) {
    return;
  }

  try {
    const cacheData = {
      data,
      timestamp: Date.now(),
    };
    window.localStorage.setItem(CACHE_KEY, JSON.stringify(cacheData));
  } catch (error) {
    console.warn('Error caching location:', error);
  }
}

/**
 * Fetch user location from ipinfo.io API
 * @returns {Promise<Object>} Location data including country code
 */
async function fetchLocation() {
  // Check if API token is available
  if (!API_TOKEN) {
    console.warn('Geolocation API token not configured - defaulting to show cookie banner');
    return { country_code: 'UNKNOWN' };
  }

  try {
    const response = await fetch(`${API_ENDPOINT}?token=${API_TOKEN}`);

    if (!response.ok) {
      throw new Error(`API request failed: ${response.status}`);
    }

    const data = await response.json();

    // Cache the result
    cacheLocation(data);

    return data;
  } catch (error) {
    console.warn('Error fetching location:', error);
    // Return a safe fallback that will show the cookie banner
    return { country_code: 'UNKNOWN' };
  }
}

/**
 * Get user location with caching
 * @returns {Promise<Object>} Location data
 */
export async function getUserLocation() {
  // Check cache first
  const cached = getCachedLocation();
  if (cached) {
    return cached;
  }

  // Fetch from API if not cached
  return await fetchLocation();
}

/**
 * Fast synchronous check if user is from US using cached data only
 * @returns {boolean|null} True if US user, false if not US, null if no cache
 */
export function isUserFromUSCached() {
  const cached = getCachedLocation();
  return cached ? cached.country_code === US_COUNTRY_CODE : null;
}

/**
 * Check if user is from the United States
 * @returns {Promise<boolean>} True if user is from US, false otherwise
 */
export async function isUserFromUS() {
  try {
    const location = await getUserLocation();
    return location.country_code === US_COUNTRY_CODE;
  } catch (error) {
    console.warn('Error checking user location:', error);
    // Default to false (show cookie banner) if there's an error
    return false;
  }
}

/**
 * Clear cached location data (useful for testing)
 */
export function clearLocationCache() {
  if (typeof window !== 'undefined' && window.localStorage) {
    try {
      window.localStorage.removeItem(CACHE_KEY);
    } catch (error) {
      console.warn('Error clearing location cache:', error);
    }
  }
}
